export interface StatementRecipientCapabilities {
  view: boolean;
  create: boolean;
  edit: boolean;
  deactivate: boolean;
  setDefault: boolean;
  validate: boolean;
  merge: boolean;
  viewAudit: boolean;
  viewReports: boolean;
}

export interface StatementRecipientAssociation {
  id: string;
  associationType: string;
  associationId: string;
  associationName: string;
  deliveryRole: string;
  isDefault: boolean;
  isManualOverride: boolean;
  priority: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StatementRecipientValidation {
  id?: string;
  status: "Valid" | "Invalid" | "Needs Review" | "Unvalidated";
  normalizedEmail?: string | null;
  issues: string[];
  duplicateRecipientId?: string | null;
  validatedAt?: string;
  validatedByName?: string;
}

export interface StatementRecipientRecord {
  id: string;
  companyId: string;
  recipientType: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  department: string;
  jobTitle: string;
  preferredDeliveryMethod: string;
  language: string;
  timeZone: string;
  status: string;
  optOut: boolean;
  sourceType: string;
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
  associations: StatementRecipientAssociation[];
  latestValidation: StatementRecipientValidation | null;
  groupMemberRecipientIds: string[];
}

export interface StatementRecipientAuditEntry {
  id: string;
  actorName: string;
  action: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string;
  occurredAt: string;
}

export interface StatementRecipientInput {
  recipientType: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  department: string;
  jobTitle: string;
  preferredDeliveryMethod: string;
  language: string;
  timeZone: string;
  status: string;
  optOut: boolean;
  sourceType: string;
  associations: Array<{
    id?: string;
    associationType: string;
    associationId: string;
    associationName: string;
    deliveryRole: string;
    isDefault: boolean;
    isManualOverride: boolean;
    priority: number;
    active: boolean;
  }>;
  groupMemberRecipientIds: string[];
  reason: string;
}

export interface StatementRecipientListResponse {
  recipients: StatementRecipientRecord[];
  summary: {
    total: number;
    active: number;
    inactive: number;
    invalid: number;
    needsReview: number;
    duplicates: number;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  capabilities: StatementRecipientCapabilities;
  reference: {
    recipientTypes: string[];
    deliveryMethods: string[];
    associationTypes: string[];
    deliveryRoles: string[];
  };
}

export interface StatementRecipientDetailResponse {
  recipient: StatementRecipientRecord;
  audit: StatementRecipientAuditEntry[];
  capabilities: StatementRecipientCapabilities;
}

export interface StatementRecipientReports {
  generatedAt: string;
  byClient: Array<{ client: string; count: number }>;
  byMethod: Array<{ method: string; count: number }>;
  invalid: StatementRecipientRecord[];
  duplicates: StatementRecipientRecord[];
  inactive: StatementRecipientRecord[];
}

const requestJson = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || `Recipient request failed with HTTP ${response.status}.`);
  }
  return payload;
};

const queryString = (values: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && String(value).trim()) params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : "";
};

export const listStatementRecipients = (
  options: {
    companyId?: string;
    search?: string;
    client?: string;
    customer?: string;
    status?: string;
    method?: string;
    validation?: string;
    updatedFrom?: string;
    updatedTo?: string;
    page?: number;
    pageSize?: number;
  } = {},
) =>
  requestJson<StatementRecipientListResponse>(
    `/api/ar-recovery/statement-recipients${queryString(options)}`,
  );

export const getStatementRecipient = (recipientId: string, companyId?: string) =>
  requestJson<StatementRecipientDetailResponse>(
    `/api/ar-recovery/statement-recipients/${encodeURIComponent(recipientId)}${queryString({
      companyId,
    })}`,
  );

export const createStatementRecipient = (input: StatementRecipientInput, companyId?: string) =>
  requestJson<StatementRecipientDetailResponse>("/api/ar-recovery/statement-recipients", {
    method: "POST",
    body: JSON.stringify({ ...input, companyId }),
  });

export const updateStatementRecipient = (
  recipientId: string,
  input: StatementRecipientInput,
  companyId?: string,
) =>
  requestJson<StatementRecipientDetailResponse>(
    `/api/ar-recovery/statement-recipients/${encodeURIComponent(recipientId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ ...input, companyId }),
    },
  );

export const validateStatementRecipient = (recipientId: string, companyId?: string) =>
  requestJson<StatementRecipientValidation>(
    `/api/ar-recovery/statement-recipients/${encodeURIComponent(recipientId)}/validate`,
    {
      method: "POST",
      body: JSON.stringify({ companyId }),
    },
  );

export const setDefaultStatementRecipient = (
  recipientId: string,
  associationId: string,
  reason: string,
  companyId?: string,
) =>
  requestJson<StatementRecipientDetailResponse>(
    `/api/ar-recovery/statement-recipients/${encodeURIComponent(recipientId)}/set-default`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, associationId, reason }),
    },
  );

export const mergeStatementRecipients = (
  sourceRecipientId: string,
  targetRecipientId: string,
  reason: string,
  companyId?: string,
) =>
  requestJson<StatementRecipientDetailResponse>("/api/ar-recovery/statement-recipients/merge", {
    method: "POST",
    body: JSON.stringify({ companyId, sourceRecipientId, targetRecipientId, reason }),
  });

export const getStatementRecipientReports = (companyId?: string) =>
  requestJson<StatementRecipientReports>(
    `/api/ar-recovery/statement-recipients/reports${queryString({ companyId })}`,
  );
